/* Stats admin panel — extracted from Admin.jsx tech-debt split. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import StripeHealthCard from "./StripeHealthCard";
import EmailHealthCard from "./EmailHealthCard";
import SearchPingCard from "./SearchPingCard";

export default function StatsPanel() {
  const [stats, setStats] = useState(null);
  const [statsRange, setStatsRange] = useState({ from: "", to: "" });
  const [statsLoading, setStatsLoading] = useState(false);

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const params = {};
      if (statsRange.from) params.from_date = statsRange.from;
      if (statsRange.to) params.to_date = statsRange.to;
      const { data } = await api.get("/admin/stats", { params });
      setStats(data);
    } catch { toast.error("Could not load stats"); }
    finally { setStatsLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchStats runs once on mount
  useEffect(() => { fetchStats(); }, []);

  return (
    <div>
      <div className="card-recipe mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-bold text-rf-ink-soft uppercase tracking-wider">From</label>
            <input
              data-testid="stats-from"
              type="date"
              value={statsRange.from}
              onChange={(e) => setStatsRange((r) => ({ ...r, from: e.target.value }))}
              className="input-rf !py-2.5 mt-1"
            />
          </div>
          <div>
            <label className="text-xs font-bold text-rf-ink-soft uppercase tracking-wider">To</label>
            <input
              data-testid="stats-to"
              type="date"
              value={statsRange.to}
              onChange={(e) => setStatsRange((r) => ({ ...r, to: e.target.value }))}
              className="input-rf !py-2.5 mt-1"
            />
          </div>
          <button data-testid="stats-apply" onClick={fetchStats} disabled={statsLoading} className="btn-ink !py-2.5">
            {statsLoading ? "Loading…" : "Apply range"}
          </button>
          <button
            data-testid="stats-clear"
            onClick={() => { setStatsRange({ from: "", to: "" }); setTimeout(fetchStats, 50); }}
            className="text-sm text-rf-orange font-bold hover:underline"
          >
            Reset (all-time)
          </button>
          {stats?.range?.from || stats?.range?.to ? (
            <span className="ml-auto text-xs font-bold text-rf-orange">
              Showing: {stats.range.from || "all"} → {stats.range.to || "now"}
            </span>
          ) : (
            <span className="ml-auto text-xs font-bold text-rf-ink-soft">Showing: all-time</span>
          )}
        </div>
      </div>

      {stats && (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {[
              ["users", "Users"], ["paid_users", "Paid users"], ["cookbooks", "Cookbooks"], ["recipes", "Recipes"],
              ["public_recipes", "Public recipes"], ["ads", "Ads"], ["ad_impressions", "Ad impressions"], ["ad_clicks", "Ad clicks"],
              ["blog_posts", "Blog posts"], ["print_orders", "Print orders"], ["visits", "Visits"],
            ].map(([k, label]) => (
              <div key={k} data-testid={`stat-${k}`} className="card-recipe">
                <p className="overline mb-1">{label}</p>
                <p className="font-display font-black text-4xl text-rf-ink">{stats[k] ?? 0}</p>
              </div>
            ))}
          </div>

          <StripeHealthCard />
          <EmailHealthCard />
          <SearchPingCard />

          <div className="card-recipe">
            <p className="overline mb-3">Visitor countries</p>
            {stats.top_countries?.length ? (
              <div className="space-y-2">
                {stats.top_countries.map((c) => {
                  const max = stats.top_countries[0].count || 1;
                  const pct = Math.round((c.count / max) * 100);
                  return (
                    <div key={c.country} className="flex items-center gap-3">
                      <span className="font-mono font-bold text-rf-ink w-14 shrink-0">{c.country}</span>
                      <div className="flex-1 bg-rf-cream-alt rounded-full h-3 overflow-hidden">
                        <div className="h-full bg-rf-orange" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="font-bold text-rf-ink-soft text-sm w-12 text-right">{c.count}</span>
                    </div>
                  );
                })}
                <p className="text-xs text-rf-ink-soft italic mt-4">Country comes from our own server (Cloudflare's visitor country, or the browser's time zone as a fallback). One event per browser session. No personal data is stored.</p>
              </div>
            ) : (
              <p className="text-rf-ink-soft text-sm">No visit data in this range yet. As real visitors land on the site, their country (e.g. GB, US, IN) will appear here.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
